import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The incident & observation RCA narrative fields a tenant can switch off
// (tenants.settings.incident_observation_fields, set by the Platform Admin).
export const RCA_FIELD_KEYS = ['root_cause', 'corrective_action', 'recommended_actions'];

export const ALL_RCA_FIELDS_ENABLED = Object.freeze({
  root_cause: true,
  corrective_action: true,
  recommended_actions: true,
});

/**
 * Pure flag resolution, mirroring backend services/tenant_field_config.py:
 * only an explicit `false` hides a field; a missing/malformed config or
 * sub-key means "shown", so unconfigured tenants are unchanged.
 */
export function resolveRcaFieldFlags(config) {
  const flags = { ...ALL_RCA_FIELDS_ENABLED };
  if (!config || typeof config !== 'object' || Array.isArray(config)) return flags;
  RCA_FIELD_KEYS.forEach((key) => {
    if (config[key] === false) flags[key] = false;
  });
  return flags;
}

/** Copy of a create/edit payload without the hidden RCA fields. */
export function stripHiddenRcaFields(payload, flags) {
  const out = { ...payload };
  RCA_FIELD_KEYS.forEach((key) => {
    if (flags && flags[key] === false) delete out[key];
  });
  return out;
}

/** True when at least one RCA field is visible for the tenant. */
export function anyRcaFieldShown(flags) {
  return RCA_FIELD_KEYS.some((key) => !flags || flags[key] !== false);
}

/**
 * Whether the detail page should offer "Generate AI Analysis" for a record.
 * With every field visible this is exactly the original rule (Root Cause and
 * Corrective Action both empty). Hidden fields are ignored, so generation is
 * never offered solely for fields the tenant does not show; when only
 * Recommended Future Actions is visible, it is offered while that is empty.
 */
export function isRcaAiAnalysisPending(record, flags) {
  if (!record || !anyRcaFieldShown(flags)) return false;
  const shown = (key) => !flags || flags[key] !== false;
  if (shown('root_cause') || shown('corrective_action')) {
    return (!shown('root_cause') || !record.root_cause)
      && (!shown('corrective_action') || !record.corrective_action);
  }
  const recs = record.recommended_actions;
  return !(recs && recs.length > 0);
}

/** Human list of the visible fields, e.g. "Root Cause, Corrective Action, and Recommended Actions". */
export function formatShownRcaLabels(flags) {
  const labels = [
    ['root_cause', 'Root Cause'],
    ['corrective_action', 'Corrective Action'],
    ['recommended_actions', 'Recommended Actions'],
  ]
    .filter(([key]) => !flags || flags[key] !== false)
    .map(([, label]) => label);
  if (labels.length <= 1) return labels.join('');
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

// Module-level cache: one fetch per login session. Keyed by the auth token
// so a different user/tenant logging in within the same SPA refetches.
let cachedToken = null;
let cachedPromise = null;

function loadFlags() {
  const token = localStorage.getItem('token');
  if (cachedPromise && cachedToken === token) return cachedPromise;
  cachedToken = token;
  cachedPromise = axios
    .get(`${API}/tenant-admin/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((response) => resolveRcaFieldFlags(response.data?.incident_observation_fields))
    .catch((err) => {
      // Fail open: show every field rather than hide data on a settings error.
      console.error('Failed to load tenant field config:', err);
      cachedPromise = null;
      return { ...ALL_RCA_FIELDS_ENABLED };
    });
  return cachedPromise;
}

/**
 * Returns {root_cause, corrective_action, recommended_actions} booleans for
 * the current tenant. All true while loading or on error. The backend
 * enforces the same config independently (never UI-only).
 */
export function useTenantFieldConfig() {
  const [flags, setFlags] = useState(ALL_RCA_FIELDS_ENABLED);

  useEffect(() => {
    let cancelled = false;
    loadFlags().then((resolved) => {
      if (!cancelled) setFlags(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return flags;
}

export default useTenantFieldConfig;
